import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/layout/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { HandCoins, RefreshCw } from "lucide-react";
import DashboardDateTimeWidget from "@/components/layout/DashboardDateTimeWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WalletPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);

  const fetchWallet = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/wallet/me`);
      setWallet(response.data.wallet);
      setTransactions(response.data.transactions || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to load wallet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallet();
  }, []);

  const creditTx = useMemo(() => transactions.filter((tx) => tx.transaction_type === "credit"), [transactions]);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar role={user?.role} />
      <main className="lg:ml-64 p-6 lg:p-10" data-testid="wallet-page">
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-heading font-bold tracking-tight">Wallet</h1>
              <p className="text-muted-foreground mt-1">Track your earned credits and transaction history.</p>
            </div>
            <div className="flex items-center gap-2">
              <DashboardDateTimeWidget />
              <Button variant="outline" onClick={fetchWallet} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardDescription>Current Balance</CardDescription></CardHeader>
              <CardContent>
                <p className="text-3xl font-heading font-bold flex items-center gap-2"><HandCoins className="w-6 h-6 text-primary" />${Number(wallet?.balance_credits || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardDescription>Total Earned</CardDescription></CardHeader>
              <CardContent>
                <p className="text-3xl font-heading font-bold text-emerald-400">${Number(wallet?.total_earned || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardDescription>Total Credit Events</CardDescription></CardHeader>
              <CardContent>
                <p className="text-3xl font-heading font-bold">{creditTx.length}</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Wallet Transactions</CardTitle>
              <CardDescription>Latest wallet credit/debit events.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Amount</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">No wallet activity yet</TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className={tx.transaction_type === "credit" ? "text-emerald-400 font-semibold" : "text-red-400 font-semibold"}>
                            {tx.transaction_type === "credit" ? "+" : "-"}${Number(tx.amount || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="capitalize">{tx.transaction_type}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{String(tx.source || "").replaceAll("_", " ")}</TableCell>
                          <TableCell>{tx.description || "-"}</TableCell>
                          <TableCell>{tx.created_at ? new Date(tx.created_at).toLocaleString() : "-"}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default WalletPage;
